import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { IInstallment } from '@/lib/builder/models/payment-plan.model';
import { InstallmentStatusBadge } from '@/lib/builder/components/payment-plan/InstallmentStatusBadge';

interface InstallmentRowProps {
    installment: IInstallment;
    /** true solo sulla rata effettivamente pagabile adesso (la prima aperta). */
    isPayable: boolean;
    isPaying: boolean;
    onPay?: (installmentId: string) => void;
    formatAmount: (value: number) => string;
    formatDate: (iso: string) => string;
    /**
     * false quando la CTA vive fuori dalla riga (la modale del planner la tiene nel
     * footer, accanto a "Chiudi"): la riga resta di sola lettura, altrimenti nella
     * stessa finestra ci sarebbero due pulsanti che pagano la stessa rata.
     */
    showPayAction?: boolean;
}

/**
 * Una rata come la vede il cliente: importo, scadenza, stato, e il pulsante di pagamento
 * quando tocca a lei. Promemoria e flag di notifica sono configurazione del TD e restano
 * fuori.
 *
 * Condivisa fra la proposta condivisa e la modale del planner: sono la stessa riga, e
 * duplicarla significherebbe che fra un mese una delle due mostra "Pagata il" e l'altra no.
 */
export const InstallmentRow = ({
    installment,
    isPayable,
    isPaying,
    onPay,
    formatAmount,
    formatDate,
    showPayAction = true,
}: InstallmentRowProps) => {
    const { t } = useI18n();

    return (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 py-2">
            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold text-gray-500 tabular-nums">
                {installment.status === 'paid' ? '✓' : installment.seq}
            </span>

            <div className="flex flex-1 flex-col min-w-0 leading-tight">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate">{installment.label}</span>
                    <InstallmentStatusBadge
                        status={installment.status}
                        isOverdue={installment.is_overdue}
                        isProcessing={installment.payment_processing}
                    />
                </div>
                <span className="text-[11px] text-gray-500">
                    {installment.status === 'paid' && installment.paid_at
                        ? t('checkout.paymentPlan.paidOn', { date: formatDate(installment.paid_at) })
                        : t('checkout.paymentPlan.dueOn', { date: formatDate(installment.due_date) })}
                </span>
            </div>

            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                {formatAmount(installment.amount)}
            </span>

            {installment.status === 'pending' && showPayAction && (
                // Il pulsante resta attivo anche con un addebito in volo: `payment_processing`
                // si accende gia' quando il cliente arriva sul bonifico in Checkout, quindi
                // toglierlo lasciava senza uscita chi non aveva concluso. Il tentativo aperto
                // e' segnalato dal badge accanto al titolo della rata.
                isPayable ? (
                    <Button
                        type="button"
                        size="sm"
                        className="h-8"
                        disabled={isPaying}
                        onClick={() => onPay?.(installment.id)}
                    >
                        {isPaying && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                        {t(
                            installment.payment_processing
                                ? 'checkout.paymentPlan.retryInstallment'
                                : 'checkout.paymentPlan.payInstallment',
                            { amount: formatAmount(installment.amount) },
                        )}
                    </Button>
                ) : (
                    <span className="text-xs text-gray-400 italic">
                        {t('checkout.paymentPlan.lockedUntilPrevious')}
                    </span>
                )
            )}
        </div>
    );
};
